import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

interface PrivacyPolicyDialogProps {
  isOpen: boolean
  onClose: () => void
}

const PrivacyPolicyDialog: React.FC<PrivacyPolicyDialogProps> = ({ isOpen, onClose }) => {

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="prose prose-gray max-w-none text-gray-900">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Information We Collect</h3>
                <p className="mb-4 text-gray-700">
                  We collect information you provide directly to us, such as when you create an account, 
                  use our services, or contact us for support. This may include your name, email address, 
                  phone number, and any other information you choose to provide.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">How We Use Your Information</h3>
                <p className="mb-4 text-gray-700">
                  We use the information we collect to provide, maintain, and improve our services, 
                  process transactions, send you technical notices and support messages, and communicate 
                  with you about products, services, and promotional offers.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Information Sharing</h3>
                <p className="mb-4 text-gray-700">
                  We do not sell, trade, or otherwise transfer your personal information to third parties 
                  without your consent, except as described in this policy. We may share your information 
                  with trusted third parties who assist us in operating our website and conducting our business.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Data Security</h3>
                <p className="mb-4 text-gray-700">
                  We implement appropriate security measures to protect your personal information against 
                  unauthorized access, alteration, disclosure, or destruction. However, no method of 
                  transmission over the internet is 100% secure.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Cookies and Tracking</h3>
                <p className="mb-4 text-gray-700">
                  We use cookies and similar tracking technologies to enhance your experience on our website, 
                  analyze usage patterns, and provide personalized content and advertisements.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Your Rights</h3>
                <p className="mb-4 text-gray-700">
                  You have the right to access, update, or delete your personal information. You may also 
                  opt out of certain communications from us. To exercise these rights, please contact us 
                  at info@logicloud.mx.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Changes to This Policy</h3>
                <p className="mb-4 text-gray-700">
                  We may update this privacy policy from time to time. We will notify you of any changes 
                  by posting the new policy on this page and updating the "Last Updated" date.
                </p>

                <h3 className="text-lg font-semibold mb-4 text-gray-900">Contact Us</h3>
                <p className="mb-4 text-gray-700">
                  If you have any questions about this privacy policy, please contact us at:
                  <br />
                  Email: info@logicloud.mx
                  <br />
                  Address: Mexico City, Mexico
                </p>

                <p className="text-sm text-gray-600 mt-6">
                  Last Updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default PrivacyPolicyDialog
